import * as React from "react";
import styled, { css } from "styled-components";
import differenceInDays from "date-fns/difference_in_days";

import { feedAvailable } from "../../lib/i18n";
import { colors, media } from "../../common/utils/style";
import { Company } from "../entities";
import WithLocale from "../../common/components/WithLocale";
import WithExperiment from "../../common/components/WithExperiment";
import Translate from "../../common/components/Translate";
import Link from "../../common/components/router/Link";

export const navFullPath = (company: Company, subpath: string): string => {
  return `/companies/${company.company_name}${subpath}`;
};

const NavItem: React.SFC<{
  to: string;
  company: Company;
  onClick: any;
  disabled?: boolean;
  showBadge?: boolean;
}> = props => {
  const { company, to, onClick, children, disabled, showBadge } = props;
  // Override fallback color
  const activeColor = company.font_color_code === colors.gray2 ? colors.gray3 : company.font_color_code;
  const activeClassName = "active-nav-link-item";

  return (
    <NavLinkItem
      to={navFullPath(company, to)}
      color={activeColor}
      activeClassName={activeClassName}
      onClick={onClick}
      disabled={disabled}
    >
      {showBadge && <Badge color={activeColor} />}
      {children}
    </NavLinkItem>
  );
};

interface Props {
  company: Company;
  isMember?: boolean;
  onClickLink?(event: React.MouseEvent<HTMLLinkElement>): void;
  innerRef(el: HTMLElement): void;
}

const HeaderNav: React.SFC<Props> = props => {
  const { company, isMember, onClickLink, innerRef } = props;

  return (
    <Nav innerRef={innerRef}>
      <NavItem to="" company={company} onClick={onClickLink}>
        <Translate id="company.nav.home" />
      </NavItem>
      <NavItem
        to="/about"
        company={company}
        onClick={onClickLink}
        disabled={
          !isMember &&
          !(Boolean(company.why_description) || Boolean(company.how_description) || Boolean(company.what_description))
        }
      >
        <Translate id="company.nav.about" />
      </NavItem>
      <NavItem to="/members" company={company} onClick={onClickLink}>
        <Translate id="company.nav.members" />
      </NavItem>
      <WithLocale>
        {({ country, locale }) =>
          feedAvailable(country) || company.is_international_feed ? (
            <NavItem
              to="/feed"
              company={company}
              onClick={onClickLink}
              disabled={!isMember && company.post_count <= 0}
              showBadge={
                !!company.lastest_post_published_at &&
                differenceInDays(new Date(), company.lastest_post_published_at) <= 5
              }
            >
              <WithExperiment name="company_nav_feed_name">
                {({ experimenting }) => {
                  if (!experimenting || locale !== "ja") {
                    return <Translate id="company.nav.posts" />;
                  }
                  switch (experimenting.variation) {
                    case 1:
                      return <>投稿</>;
                    case 2:
                      return <>記事</>;
                    default:
                      return <Translate id="company.nav.posts" />;
                  }
                }}
              </WithExperiment>
            </NavItem>
          ) : null
        }
      </WithLocale>
      <NavItem
        to="/projects"
        company={company}
        onClick={onClickLink}
        disabled={!isMember && company.project_count <= 0}
      >
        <Translate id="company.nav.projects" />
      </NavItem>
    </Nav>
  );
};

const Nav = styled.nav`
  display: flex;
`;

interface StyledProps {
  color: string;
  activeClassName: string;
  disabled?: boolean;
}

const NavLinkItem = styled(Link)`
  display: inline-block;
  flex-shrink: 0;
  position: relative;
  font-weight: 500;
  margin-right: 18px;
  padding: 13px 0;
  color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray1)};
  cursor: ${props => (props.disabled ? "default" : "pointer")};
  &:link,
  &:visited {
    color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray1)};
  }
  &:last-child {
    margin-right: 0;
  }
  &:hover {
    text-decoration: none;
    opacity: ${props => (props.disabled ? 1.0 : 0.5)};
  }
  @media ${media.large} {
    padding: 8px 0;
    color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray4)};
    transition: opacity 100ms;
    &:link,
    &:visited {
      color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray4)};
    }
  }
  &.${(props: StyledProps) => props.activeClassName} {
    font-weight: 600;
    color: ${(props: StyledProps) => props.color};
    &:before {
      position: absolute;
      width: 18px;
      height: 2px;
      border-radius: 1px;
      background-color: ${(props: StyledProps) => props.color};
      content: "";
      display: inline-block;
      bottom: -1px;
      left: 50%;
      transform: translateX(-50%);
    }
    @media ${media.large} {
      color: inherit;
      &:hover {
        opacity: 1;
      }
    }
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 3px;
  left: -6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(props: { color: string }) => props.color};
`;

export default HeaderNav;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/HeaderNav.tsx